import React, { useMemo, useState } from "react";
import { Search, Store as StoreIcon } from "lucide-react";
import { Input } from "../../components/ui/input";
import { BulkCheckbox } from "./BulkSelectContext";
import { TYPE_LABELS } from "./constants";

export default function MagazieStockView({ items }) {
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  const magazieItems = useMemo(
    () => items.filter((i) => (i.location || "").toUpperCase() === "MAGAZIE"),
    [items]
  );

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return magazieItems.filter((i) => {
      if (typeFilter !== "all" && (i.item_type || "echipament") !== typeFilter) return false;
      if (!needle) return true;
      return [i.name, i.code, i.brand, i.model, i.color]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle));
    });
  }, [magazieItems, q, typeFilter]);

  const grouped = useMemo(() => {
    const out = { echipament: [], consumabil: [], psa: [] };
    for (const it of filtered) {
      const t = it.item_type || "echipament";
      (out[t] = out[t] || []).push(it);
    }
    for (const k of Object.keys(out)) out[k].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return out;
  }, [filtered]);

  const counts = useMemo(() => {
    const c = { all: magazieItems.length, echipament: 0, consumabil: 0, psa: 0 };
    for (const it of magazieItems) c[it.item_type || "echipament"] = (c[it.item_type || "echipament"] || 0) + 1;
    return c;
  }, [magazieItems]);

  const totalQty = useMemo(
    () => filtered.reduce((s, i) => s + (Number(i.quantity) || 0), 0),
    [filtered]
  );

  return (
    <div className="space-y-5" data-testid="magazie-stock-view">
      {/* Header summary */}
      <div className="card-flat p-5 flex flex-wrap items-center gap-4 justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-md bg-[var(--primary-subtle)] text-[var(--primary)] grid place-items-center">
            <StoreIcon size={22}/>
          </div>
          <div>
            <div className="font-display text-xl font-bold leading-tight">Magazie</div>
            <div className="text-xs text-zinc-500 mt-0.5">
              {magazieItems.length} produse · {totalQty.toLocaleString("ro-RO")} buc/UM total
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { k: "all", label: "Toate" },
            { k: "echipament", label: "Echipamente" },
            { k: "consumabil", label: "Consumabile" },
            { k: "psa", label: "PSA" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTypeFilter(t.k)}
              data-testid={`magazie-filter-${t.k}`}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold tracking-wide transition-colors ${
                typeFilter === t.k
                  ? "bg-[var(--primary)] text-white"
                  : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
              }`}
            >
              {t.label} <span className="ml-1 opacity-70">({counts[t.k] || 0})</span>
            </button>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-2.5 top-2.5 text-zinc-400"/>
          <Input
            data-testid="magazie-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Caută în Magazie (denumire, cod, marcă)..."
            className="pl-8 h-9 text-sm"
          />
        </div>
        <span className="text-xs text-zinc-500 ml-auto">
          {filtered.length} / {magazieItems.length} afișate
        </span>
      </div>

      {/* Items grouped by type */}
      {magazieItems.length === 0 ? (
        <div className="card-flat p-10 text-center text-sm text-zinc-500">
          Nu există produse în Magazie momentan.
        </div>
      ) : filtered.length === 0 ? (
        <div className="card-flat p-10 text-center text-sm text-zinc-500">
          Niciun produs nu corespunde filtrelor curente.
        </div>
      ) : (
        <div className="space-y-6">
          {["echipament", "consumabil", "psa"].map((t) => {
            const list = grouped[t] || [];
            if (list.length === 0) return null;
            return (
              <div key={t}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-zinc-600">
                    {TYPE_LABELS[t] || t}
                  </span>
                  <span className="text-xs text-zinc-400">·</span>
                  <span className="text-xs text-zinc-500">{list.length} produse</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {list.map((it) => (
                    <div key={it.id} className="card-flat p-4 relative" data-testid={`magazie-item-${it.id}`}>
                      <BulkCheckbox id={it.id} className="absolute top-1.5 left-1.5 z-10"/>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-semibold text-sm truncate">{it.name}</div>
                          {it.code && (
                            <div className="text-[11px] text-zinc-400 font-mono mt-0.5">{it.code}</div>
                          )}
                        </div>
                        <span className="shrink-0 px-2 py-0.5 rounded text-[10px] font-bold bg-zinc-100 text-zinc-700 tabular-nums">
                          {Number(it.quantity || 0).toLocaleString("ro-RO")} {it.unit || "buc"}
                        </span>
                      </div>
                      {(it.brand || it.model || it.color) && (
                        <div className="text-xs text-zinc-500 mt-1.5 truncate">
                          {[it.brand, it.model, it.color].filter(Boolean).join(" · ")}
                        </div>
                      )}
                      {it.supplier && (
                        <div className="text-[11px] text-blue-700 mt-1 truncate" title={`Furnizor: ${it.supplier}`}>
                          🏭 {it.supplier}
                        </div>
                      )}
                      {it.assignee && (
                        <div className="mt-1.5 px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-[11px]">
                          <div className="text-emerald-800 font-bold flex items-center gap-1">
                            <span>👤</span>
                            <span className="truncate">{it.assignee}</span>
                          </div>
                          {it.assigned_at && (
                            <div className="text-[10px] text-emerald-700 mt-0.5">
                              📅 din <strong>{it.assigned_at.slice(8,10)}/{it.assigned_at.slice(5,7)}/{it.assigned_at.slice(0,4)}</strong>
                            </div>
                          )}
                          {it.assigned_by && <div className="text-[10px] text-emerald-600">predat de <strong>{it.assigned_by}</strong></div>}
                          {it.recipient_car_label && <div className="text-[10px] text-emerald-700 font-bold">🚗 {it.recipient_car_label}</div>}
                          {it.recipient_note && <div className="italic text-[10px] text-emerald-600">{it.recipient_note}</div>}
                        </div>
                      )}
                      {!it.assignee && it.assigned_at && (
                        <div className="text-[10px] text-zinc-500 mt-1 pt-1 border-t border-zinc-100" data-testid={`item-allocation-card-${it.id}`}>
                          <div>📍 la locație din <strong>{it.assigned_at.slice(8,10)}/{it.assigned_at.slice(5,7)}/{it.assigned_at.slice(0,4)}</strong></div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
